import { useState } from "react";
import { useTranslation } from "@localization/useTranslation";
import { RelativeDateConfigModel, RelativeDateDirection, RelativeDateReference } from "../../../annualWheel/RelativeDate.types";
import { relativeDateUnitTranslationKeys } from "@common/relativeDateCalculation";
import { RelativeDateConfigProps } from "./RelativeDateConfig";

function getRelativeDateSummaryTranslationKey(config: RelativeDateConfigModel) {
    const isBefore = config.direction === RelativeDateDirection.Before;
    if (config.relativeTo === RelativeDateReference.ContractStart) {
        return isBefore ? "relativeDateBeforeContractStart" : "relativeDateAfterContractStart";
    }

    return isBefore ? "relativeDateBeforeContractEnd" : "relativeDateAfterContractEnd";
}

export function useRelativeDateConfig(props: RelativeDateConfigProps) {
    const { translateString, translateDate } = useTranslation();
    // State rather than a ref, because the field it anchors to first appears together with the popper
    // when a relative date is switched on, and a ref would still be empty on that render.
    const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    // Only offer a relative date when the contract has at least one date to count from
    const hasContractDates = !!props.contractStartDate || !!props.contractEndDate;

    function getRelativeToOptions() {
        const options = [];
        if (props.contractStartDate) {
            options.push({
                id: RelativeDateReference.ContractStart.toString(),
                name: `${translateString("contractStartDate")} (${translateDate(props.contractStartDate)})`,
            });
        }
        if (props.contractEndDate) {
            options.push({
                id: RelativeDateReference.ContractEnd.toString(),
                name: `${translateString("contractEndDate")} (${translateDate(props.contractEndDate)})`,
            });
        }
        return options;
    }

    function getSummary() {
        if (!props.config) {
            return null;
        }

        const amount = translateString(relativeDateUnitTranslationKeys[props.config.unit], { count: props.config.value });
        return translateString(getRelativeDateSummaryTranslationKey(props.config), { amount });
    }

    function openEditor() {
        if (!props.readOnly) {
            setIsEditing(true);
        }
    }

    function onSpecifyRelativeDateClick() {
        props.onToggleRelativeDate();
        setIsEditing(true);
    }

    return {
        anchorElement,
        setAnchorElement,
        isEditing,
        hasContractDates,
        relativeToOptions: getRelativeToOptions(),
        summary: getSummary(),
        openEditor,
        onSpecifyRelativeDateClick,
        closeEditor: () => setIsEditing(false),
    };
}
